/**
 * Criar viagem.
 *
 * O produto não sabe nada sobre nenhuma viagem específica: nome, destino,
 * datas e fuso são o que a pessoa digita, e duração, dia e fase são derivados
 * disso. Nada aqui tem valor pré-preenchido de uma viagem de referência — a
 * única sugestão é o fuso do próprio aparelho, que é uma comodidade e pode ser
 * trocada.
 *
 * Quem cria vira Owner **desta** viagem. O servidor grava viagem e vínculo na
 * mesma transação; a tela só envia, e envia com um id gerado aqui — assim um
 * toque duplo ou uma retentativa não produzem duas viagens iguais.
 */
import { TRIP_CURRENCIES, TRIP_CURRENCY_LABELS, type TripCurrency, type TripFeature, uuidv7 } from '@nv/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiError, createTrip, setTripFeature, updateTrip } from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { guardarViagemAtiva, lerViagemAtiva } from '../../trip/viagemAtiva';
import { BarraVoltar } from '../../ui/BarraVoltar';
import { Icon } from '../../ui/icons';
import { Card, Row, SectionLabel, Segmented } from '../../ui/primitives';
import { Sheet } from '../../ui/Sheet';
import { RecorteDeImagem } from '../../ui/RecorteDeImagem';
import { Field } from '../auth/Field';
import { LocalField, type LugarDaAtividade } from '../roteiro/LocalField';
import { ListaDeFuncionalidades } from './FuncionalidadesDaViagem';
import { enviarCapa } from './capa';
import { FUSOS } from './opcoes';


export function CriarViagemScreen() {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? '');

  const fusoDoAparelho = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
    } catch {
      return 'America/Sao_Paulo';
    }
  }, []);

  const opcoesDeFuso = useMemo(
    () => [...new Set([fusoDoAparelho, ...FUSOS])],
    [fusoDoAparelho],
  );

  const [nome, setNome] = useState('');
  const [destino, setDestino] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [fuso, setFuso] = useState(fusoDoAparelho);
  // Tudo que Configurações oferece já está aqui (PO, 19/09): funcionalidades,
  // hora do resumo e hospedagem — opcionais, aplicados logo após a criação.
  const [desligadas, setDesligadas] = useState<Set<TripFeature>>(new Set());
  const [horaDoResumo, setHoraDoResumo] = useState('21:00');
  const [hospedagem, setHospedagem] = useState('');
  const [endereco, setEndereco] = useState('');
  const [lugarDaHospedagem, setLugarDaHospedagem] = useState<LugarDaAtividade | null>(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [instrucoes, setInstrucoes] = useState('');
  // Imagem da viagem (item 22): escolhida e recortada aqui, enviada depois
  // que a viagem existe — ela precisa de um id para ter uma imagem.
  const [imagemBruta, setImagemBruta] = useState<File | null>(null);
  const [capa, setCapa] = useState<{ blob: Blob; url: string } | null>(null);
  const arquivoDaCapa = useRef<HTMLInputElement>(null);
  // Sem padrão: a moeda é uma escolha explícita de quem cria (USD, BRL ou EUR).
  const [moeda, setMoeda] = useState<TripCurrency | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [aberta, setAberta] = useState<'funcionalidades' | 'resumo' | 'hospedagem' | 'fuso' | null>(null);

  // Intervalo fechado: uma viagem de um dia tem início igual ao fim.
  const datasInvertidas = inicio.length > 0 && fim.length > 0 && fim < inicio;
  const podeEnviar =
    nome.trim().length > 0 &&
    destino.trim().length > 0 &&
    inicio.length > 0 &&
    fim.length > 0 &&
    moeda !== null &&
    !datasInvertidas;

  async function criar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    setEnviando(true);
    setErro(null);

    try {
      const viagem = await createTrip({
        id: uuidv7(),
        name: nome.trim(),
        primaryDestination: destino.trim(),
        startDate: inicio,
        endDate: fim,
        timezone: fuso,
        baseCurrency: moeda ?? undefined,
      });

      // A imagem sobe depois: se falhar, a viagem existe do mesmo jeito e a
      // imagem se põe em Configurações.
      if (capa) await enviarCapa(viagem.id, capa.blob).catch(() => undefined);

      // O resto das configurações, pelos mesmos caminhos de Configurações.
      // Se algo falhar aqui, a viagem já existe: a pessoa ajusta lá depois.
      let completa = viagem;
      const temHospedagem = hospedagem.trim() || endereco.trim() || checkIn || checkOut || instrucoes.trim();
      if (horaDoResumo !== '21:00' || temHospedagem) {
        completa = await updateTrip(viagem.id, {
          summaryTime: horaDoResumo,
          accommodationName: hospedagem.trim() || null,
          accommodationAddress: endereco.trim() || null,
          accommodationPlaceId: lugarDaHospedagem?.placeId ?? null,
          accommodationLatitude: lugarDaHospedagem?.latitude ?? null,
          accommodationLongitude: lugarDaHospedagem?.longitude ?? null,
          accommodationCheckIn: checkIn || null,
          accommodationCheckOut: checkOut || null,
          accommodationNotes: instrucoes.trim() || null,
        }).catch(() => viagem);
      }
      for (const f of desligadas) {
        const r = await setTripFeature(viagem.id, f, false).catch(() => null);
        if (r) completa = { ...completa, features: r.features };
      }

      // A resposta do servidor inteira: é dela que a Home tira nome, destino,
      // datas e fuso. Guardar um recorte obrigaria a tela a completar o resto.
      guardarViagemAtiva(userId, completa);
      await cliente.invalidateQueries({ queryKey: ['viagens'] });
      navegar('/', { replace: true });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível criar a viagem agora.');
    } finally {
      setEnviando(false);
    }
  }

  const { key: chaveDeNavegacao } = useLocation();
  const temVolta = chaveDeNavegacao !== 'default' || lerViagemAtiva(userId) !== null;

  // Essencial + opcional (refino final): 5 campos obrigatórios com contador e
  // barrinhas; o resto (imagem, funcionalidades, resumo, hospedagem, fuso)
  // vira linhas que abrem folhas — o conteúdo de cada uma é o mesmo de antes.
  const essenciais = [nome.trim().length > 0, destino.trim().length > 0, inicio.length > 0, fim.length > 0 && !datasInvertidas, moeda !== null];
  const feitos = essenciais.filter(Boolean).length;
  const duracao = inicio && fim && !datasInvertidas ? Math.round((Date.parse(fim) - Date.parse(inicio)) / 86_400_000) + 1 : null;
  const temHospedagem = Boolean(hospedagem.trim() || endereco.trim() || checkIn || checkOut || instrucoes.trim());

  return (
    <section className={temVolta ? 'viagens viagens--com-voltar criar-viagem' : 'viagens criar-viagem'}>
      {temVolta && <BarraVoltar />}
      <form className="auth-form criar-viagem__form" onSubmit={criar}>
        <span className="criar-viagem__contador">{feitos} de 5 essenciais</span>
        <div className="criar-viagem__topo">
          <button type="button" className="criar-viagem__avatar-botao" onClick={() => arquivoDaCapa.current?.click()} aria-label="Escolher a imagem da viagem" data-testid="capa-escolher">
            {capa ? (
              <img className="criar-viagem__avatar" src={capa.url} alt="" />
            ) : (
              <span className={nome.trim() ? 'criar-viagem__avatar criar-viagem__avatar--com-nome' : 'criar-viagem__avatar'} aria-hidden="true">
                {(nome.trim()[0] ?? '').toUpperCase()}
              </span>
            )}
            <span className="criar-viagem__lapis" aria-hidden="true"><Icon name="editar" size={12} /></span>
          </button>
          <span className="criar-viagem__titulos">
            <h1 className={nome.trim() ? 'criar-viagem__titulo' : 'criar-viagem__titulo criar-viagem__titulo--vazio'}>{nome.trim() || 'Nova viagem'}</h1>
            <span className="criar-viagem__sub viagens__capa-texto">{capa ? 'Imagem escolhida · toque para trocar' : 'Imagem opcional · aparece em Minhas viagens'}</span>
          </span>
          <input ref={arquivoDaCapa} type="file" accept="image/*" hidden data-testid="capa-arquivo" onChange={(e) => setImagemBruta(e.target.files?.[0] ?? null)} />
        </div>
        <span className="criar-viagem__barras" aria-hidden="true">
          {essenciais.map((ok, i) => (
            <span key={i} className={ok ? 'criar-viagem__barra criar-viagem__barra--ok' : 'criar-viagem__barra'} />
          ))}
        </span>
        {imagemBruta && (
          <RecorteDeImagem
            arquivo={imagemBruta}
            titulo="Imagem da viagem"
            onCancelar={() => setImagemBruta(null)}
            onConfirmar={(blob) => {
              if (capa) URL.revokeObjectURL(capa.url);
              setCapa({ blob, url: URL.createObjectURL(blob) });
              setImagemBruta(null);
            }}
          />
        )}

        <Field
          label="Nome da viagem"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Como o grupo chama esta viagem"
          required
        />
        {/* Destino com sugestões do Maps (cidade/estado/país) — PO, 19/09. Sem Maps, é texto. */}
        <LocalField rotulo="Destino principal" modo="destino" nome={destino} lugar={null} onChange={(texto) => setDestino(texto)} required />

        <div className={datasInvertidas ? 'auth-form__row criar-viagem__datas criar-viagem__datas--erro' : 'auth-form__row criar-viagem__datas'}>
          <Field
            label="Início"
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            required
          />
          <Field
            label="Término"
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            required
          />
        </div>
        {datasInvertidas && <p className="criar-viagem__aviso criar-viagem__aviso--erro" role="alert">O término não pode ser antes do início.</p>}
        {duracao !== null && <p className="criar-viagem__aviso">{duracao === 1 ? '1 dia de viagem' : `${duracao} dias de viagem`}</p>}

        <SectionLabel>Moeda da viagem</SectionLabel>
        <Segmented
          value={moeda}
          options={TRIP_CURRENCIES.map((m) => ({ valor: m, rotulo: `${TRIP_CURRENCY_LABELS[m].simbolo} ${m}`, testId: `moeda-${m}` }))}
          onChange={setMoeda}
          label="Moeda da viagem"
        />

        <Card variant="surf" lista className="criar-viagem__opcionais">
          <Row title="Imagem da viagem" detail={capa ? 'Escolhida' : 'Opcional'} onClick={() => arquivoDaCapa.current?.click()} />
          <Row title="Funcionalidades" detail={desligadas.size === 0 ? 'Tudo ligado' : `${desligadas.size} ${desligadas.size === 1 ? 'desligada' : 'desligadas'}`} onClick={() => setAberta('funcionalidades')} data-testid="criar-funcionalidades" />
          <Row title="Resumo do dia" detail={`às ${horaDoResumo}`} onClick={() => setAberta('resumo')} data-testid="criar-resumo" />
          <Row title="Hospedagem" detail={temHospedagem ? (hospedagem.trim() || 'Preenchida') : 'Opcional'} onClick={() => setAberta('hospedagem')} data-testid="criar-hospedagem" />
          <Row title="Fuso da viagem" detail={`${fuso.replace(/_/g, ' ')}${fuso === fusoDoAparelho ? ' (deste aparelho)' : ''}`} onClick={() => setAberta('fuso')} data-testid="criar-fuso" />
        </Card>

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}

        <div className="criar-viagem__cta">
          <button
            type="submit"
            className="nv-btn nv-btn--primary nv-btn--block"
            disabled={!podeEnviar || enviando}
          >
            {enviando ? 'Criando…' : 'Criar viagem'}
          </button>
        </div>
      </form>

      {aberta === 'funcionalidades' && (
        <Sheet titleId="criar-func" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="criar-func">Funcionalidades</h2>
          <ListaDeFuncionalidades
            ligadas={(f) => !desligadas.has(f)}
            onAlternar={(f, ligar) =>
              setDesligadas((atual) => {
                const prox = new Set(atual);
                if (ligar) prox.delete(f);
                else prox.add(f);
                return prox;
              })
            }
            nota="Tudo vem ligado. O que desligar aqui já nasce desligado; dá para mudar depois em Configurações."
          />
          <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => setAberta(null)}>Pronto</button>
        </Sheet>
      )}
      {aberta === 'resumo' && (
        <Sheet titleId="criar-resumo" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="criar-resumo">Resumo do dia</h2>
          <label className="field">
            <span className="field__label">Hora em que o resumo fica pronto</span>
            <input className="field__input" type="time" value={horaDoResumo} onChange={(e) => setHoraDoResumo(e.target.value)} aria-label="Hora do resumo do dia" data-testid="hora-do-resumo" />
          </label>
          <p className="auth-note">No fuso da viagem. Nessa hora o grupo recebe "O dia de hoje está pronto".</p>
          <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => setAberta(null)}>Pronto</button>
        </Sheet>
      )}
      {aberta === 'hospedagem' && (
        <Sheet titleId="criar-hosp" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="criar-hosp">Hospedagem</h2>
          <p className="auth-note" style={{ marginTop: 0 }}>Opcional — dá para preencher depois em Configurações.</p>
          <Field label="Nome da hospedagem" value={hospedagem} onChange={(e) => setHospedagem(e.target.value)} placeholder="Onde o grupo fica" />
          <LocalField
            rotulo="Endereço"
            modo="endereco"
            nome={endereco}
            lugar={lugarDaHospedagem}
            onChange={(texto, lugar) => {
              setEndereco(texto);
              setLugarDaHospedagem(lugar);
            }}
          />
          <div className="auth-form__row">
            <Field label="Check-in" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="hosp-checkin" />
            <Field label="Check-out" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="hosp-checkout" />
          </div>
          <label className="nv-field">
            <span className="nv-field__label">Instruções</span>
            <textarea className="nv-field__input fb__texto" rows={3} value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)} placeholder="Código da porta, Wi-Fi, onde fica a chave…" data-testid="hosp-instrucoes" />
          </label>
          <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => setAberta(null)}>Pronto</button>
        </Sheet>
      )}
      {aberta === 'fuso' && (
        <Sheet titleId="criar-fuso" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="criar-fuso">Fuso da viagem</h2>
          <p className="auth-note" style={{ marginTop: 0 }}>O "hoje" do roteiro e os horários seguem este fuso.</p>
          <select
            className="fin-nova__select criar-viagem__fuso"
            value={fuso}
            onChange={(e) => setFuso(e.target.value)}
            aria-label="Fuso da viagem"
          >
            {opcoesDeFuso.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
                {z === fusoDoAparelho ? ' (deste aparelho)' : ''}
              </option>
            ))}
          </select>
          <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => setAberta(null)}>Pronto</button>
        </Sheet>
      )}
    </section>
  );
}
